<template>
  <div>

    <v-progress-linear
      v-if="loadingForms"
      indeterminate
      color="#107569"
      height="3"
    />


    <Treeselect
      v-model="selectedFormIds"
      :multiple="true"
      :options="formattedForms"
      :disable-branch-nodes="true"
      placeholder="Select one form per platform"
      :searchable="true"
      :clearable="true"
      :normalizer="normalizer"
      class="leads-select"
      @open="handleOpen"
      @input="handleSelection"
    />
  </div>
</template>

<script>
import Treeselect from "vue3-treeselect";
import "vue3-treeselect/dist/vue3-treeselect.css";
import apiClient from "@/core/buildRequest";
import { useSuperAddStore } from "@/store/superAddStore";

export default {
  components: { Treeselect },
  data() {
    return {
      formattedForms: [],
      store: useSuperAddStore(),

      loadingForms: false,

      selectedFormIds: [],
    };
  },
  watch: {
    selectedFormIds(newVal) {
      const platformSelected = {};
      const filtered = [];

      newVal
        .slice()
        .reverse()
        .forEach((formId) => {
          const platform = this.getPlatformById(formId);
          if (platform && !platformSelected[platform]) {
            platformSelected[platform] = true;
            filtered.unshift(formId);
          }
        });

      if (JSON.stringify(this.selectedFormIds) !== JSON.stringify(filtered)) {
        this.selectedFormIds = filtered;
      }

      const finalSelected = filtered.map((formId) => ({
        id: formId,
        platform: this.getPlatformById(formId),
      }));

      this.store.selectedForms = finalSelected;
    },
  },
  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.label,
        isDisabled: node.disabled || false,
      };
    },

    getPlatformById(formId) {
      for (const group of this.formattedForms) {
        if (group.children?.some((c) => c.id === formId)) return group.id;
      }
      return null;
    },

    handleSelection(newVal) {
      this.selectedFormIds = newVal;
    },

    async handleOpen() {
      if (this.formattedForms.length > 0) return;

      this.loadingForms = true;

      const platformsRaw = localStorage.getItem("super_selected_platforms");
      const facebookAccountsRaw = localStorage.getItem(
        "selected_identity_accounts"
      );
      const otherAccountsRaw = localStorage.getItem("superselected_accounts");

      if (!platformsRaw || !facebookAccountsRaw || !otherAccountsRaw) return;

      const selectedPlatforms = JSON.parse(platformsRaw)?.map((p) => {
        const name = p.name?.toLowerCase();
        return name === "meta" ? "facebook" : name;
      });

      const facebookAccounts = JSON.parse(facebookAccountsRaw);
      const otherAccounts = JSON.parse(otherAccountsRaw);

      const params = {};
      const platformsList = [];

      if (
        selectedPlatforms.includes("facebook") &&
        facebookAccounts.facebook?.id
      ) {
        params.facebook_page_id = facebookAccounts.facebook.id;
        platformsList.push("facebook");
      }

      if (
        selectedPlatforms.includes("snapchat") &&
        otherAccounts.snapchat?.id
      ) {
        params.snapchat_ad_account_id = otherAccounts.snapchat.id;
        platformsList.push("snapchat");
      }

      if (selectedPlatforms.includes("tiktok") && otherAccounts.tiktok?.id) {
        params.tiktok_ad_account_id = otherAccounts.tiktok.id;
        platformsList.push("tiktok");
      }

      platformsList.forEach((p, i) => {
        params[`platforms[${i}]`] = p;
      });

      try {
        const response = await apiClient.get("lead-center/get/lead-forms", {
          params,
        });

        const data = response.data?.data?.success || [];

        const platformsMap = {
          facebook: { label: "Meta", children: [] },
          snapchat: { label: "Snapchat", children: [] },
          tiktok: { label: "TikTok", children: [] },
        };

        data.forEach((form) => {
          const platform = form.platform?.toLowerCase();
          if (platformsMap[platform]) {
            platformsMap[platform].children.push({
              id: form.id,
              label: form.name,
            });
          }
        });

        this.formattedForms = Object.entries(platformsMap)
          .filter(([key]) => selectedPlatforms.includes(key))
          .map(([key, group]) => ({
            id: key,
            label: group.label,
            children: group.children,
          }));
      } catch (err) {
        console.error("❌ Failed to load lead forms:", err);

      } finally {
        this.loadingForms = false;

      }
    },

    validate() {
      const errors = [];

      if (!this.formattedForms.length) {
        errors.push("Please select lead.");
        return errors;
      }

      const allSelected = this.formattedForms.every((platformGroup) =>
        platformGroup.children.some((form) =>
          this.selectedFormIds.includes(form.id)
        )
      );

      if (!allSelected) {
        errors.push("Please select at least one lead form for all platforms.");
      }

      return errors;
    },

    getData() {
      return {
        selectedForms: this.selectedFormIds,
      };
    },
  },
};
</script>

<style scoped>
.leads-select {
  min-height: 3rem;
}

::v-deep(.vue-treeselect__control) {
  min-height: 3rem;
}
</style>
